import { Icon } from "@astryxdesign/core/Icon";
import { useRef, useState, type ReactNode } from "react";
import { CheckIcon, TrashIcon } from "../lib/icons";

interface Props {
  /** Swipe right (reveals a check on the left). */
  onSwipeRight: () => void;
  /** Swipe left (reveals a trash can on the right). */
  onSwipeLeft: () => void;
  /** While a reorder drag is active, swiping must not fight the drag. */
  isDragging?: boolean;
  children: ReactNode;
}

const TRIGGER_PX = 72;
const MAX_PX = 110;

/**
 * Touch-only swipe wrapper for list rows (Astryx has no swipe primitive, so
 * this is hand-rolled with token colors). Renders as <li> so it can live
 * directly inside the board's <ul>. Vertical scrolling stays native via
 * touch-action: pan-y; a horizontal drag past the threshold fires the action.
 */
export function SwipeableRow({ onSwipeRight, onSwipeLeft, isDragging, children }: Props) {
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const axis = useRef<"h" | "v" | null>(null);
  const suppressClick = useRef(false);

  function onTouchStart(e: React.TouchEvent) {
    const t = e.touches[0];
    if (!t || e.touches.length > 1 || isDragging) return;
    start.current = { x: t.clientX, y: t.clientY };
    axis.current = null;
  }

  function onTouchMove(e: React.TouchEvent) {
    const t = e.touches[0];
    if (!t || !start.current) return;
    const rawDx = t.clientX - start.current.x;
    const rawDy = t.clientY - start.current.y;
    if (axis.current === null) {
      if (Math.abs(rawDx) < 12 && Math.abs(rawDy) < 12) return;
      axis.current = Math.abs(rawDx) > Math.abs(rawDy) * 1.2 ? "h" : "v";
      if (axis.current === "h") setDragging(true);
    }
    if (axis.current !== "h") return;
    setDx(Math.max(-MAX_PX, Math.min(MAX_PX, rawDx)));
  }

  function onTouchEnd() {
    if (axis.current === "h") {
      if (Math.abs(dx) > 10) suppressClick.current = true;
      if (dx > TRIGGER_PX) onSwipeRight();
      else if (dx < -TRIGGER_PX) onSwipeLeft();
    }
    start.current = null;
    axis.current = null;
    setDragging(false);
    setDx(0);
  }

  return (
    <li
      style={{ listStyle: "none", position: "relative", overflow: "hidden", touchAction: "pan-y" }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onClickCapture={(e) => {
        if (suppressClick.current) {
          e.preventDefault();
          e.stopPropagation();
          suppressClick.current = false;
        }
      }}
    >
      {dx !== 0 ? (
        <span
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: dx > 0 ? "flex-start" : "flex-end",
            paddingInline: "var(--spacing-4)",
            background: dx > 0 ? "var(--color-success-muted)" : "var(--color-error-muted)",
          }}
        >
          <Icon icon={dx > 0 ? CheckIcon : TrashIcon} color={dx > 0 ? "success" : "error"} />
        </span>
      ) : null}
      <span
        style={{
          display: "block",
          transform: `translateX(${dx}px)`,
          transition: dragging ? "none" : "transform 0.18s ease",
          background: dx !== 0 ? "var(--color-background-body)" : undefined,
        }}
      >
        {children}
      </span>
    </li>
  );
}
